/**
 * Helpers for resolving the productId to load into RecsCarouselTile's
 * Quick View.
 *
 * Lives in its own file so unit tests can import it without dragging in
 * React + the recs tile's render tree, and so `react-refresh/only-export-
 * components` stays happy on `recs-carousel-tile.tsx`.
 *
 * SFCC-11590 history:
 *   - First pass: gated Quick View on `isMasterProduct(product)` only.
 *   - QA pass 1 failure: CIO's recs pods can return either master IDs or
 *     child variant SKUs, so half the multi-variant recommendations had
 *     no QV affordance.
 *   - QA pass 2 directive: show Quick View on ALL recs tiles (master,
 *     variant, and simple). Variant hits still need to open against the
 *     parent master so the shopper can see/pick other variants; simple
 *     hits open against themselves (QV renders like a mini-PDP).
 */
import type { HecProduct } from "~/hec/types/product";
import { isMasterProduct } from "~/hec/types/product";

/**
 * Resolve the productId to load into Quick View for a recs tile.
 *
 * QA requires QV on every recs tile regardless of product type, so this
 * never returns `null` — it always yields a valid productId. The only
 * variation is which id we open QV against:
 *
 *   1. Hit IS the master                     → product.id (master itself)
 *   2. Hit carries `master.masterId`         → parent master (SCAPI variant)
 *   3. Hit carries `c_parentMasterProductID` → parent master (custom attr)
 *   4. Otherwise (simple product)            → product.id (no variants)
 *
 * For variant hits, returning the master id lets QV present every variant
 * — not just the single SKU CIO happened to recommend. For simple hits,
 * QV behaves as a mini-PDP for that product (matching PLP tile behavior).
 *
 * Master-field propagation depends on `productHitToHecProduct` carrying
 * `master.masterId` + `c_parentMasterProductID` through the CIO → SCAPI →
 * HecProduct adapter. See `app/hec/utils/product-adapters.ts`.
 */
export function resolveMasterIdForQuickView(product: HecProduct): string {
  if (isMasterProduct(product)) return product.id;
  if (product.master?.masterId) return product.master.masterId;
  if (product.c_parentMasterProductID) return product.c_parentMasterProductID;
  return product.id;
}
